import { useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Printer,
  Sparkles,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import {
  GET_SITUATION_SESSION,
  GET_SITUATION_SESSION_FOR_GROWNUP,
} from "@/graphql/queries/situations";
import {
  DELETE_SITUATION_SESSION,
  RESET_SITUATION_SESSION_PERSONALIZATION,
} from "@/graphql/mutations/situations";
import { SituationPersonalizeDialog } from "@/components/situations/situation-personalize-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/lib/auth/AuthContext";

type Row = {
  id: string;
  key: string;
  title: string;
  themeLabels: string[];
  answeredCount: number;
  questionCount: number;
  needsPracticeCount?: number | null;
  submittedAt?: string | null;
};

/**
 * One session: the situations in it, in order, and how far through them the
 * learner is.
 *
 * **The progress here is „how many have been written in", never „how many were
 * right".** There is no right answer in this section, no level and nothing to
 * unlock — so the child and the grown-up read the same number, and it is a count
 * of sheets rather than a percentage.
 *
 * A grown-up gets three things the learner does not: rewording the whole set,
 * printing it, and throwing the assignment away. All three are about the
 * assignment; none of them can reach what the child wrote, which hangs off the
 * problem rather than off this session.
 */
export default function SituationSessionPage() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { student: me, isGuardian } = useAuth();

  const { data, loading } = useQuery(
    isGuardian ? GET_SITUATION_SESSION_FOR_GROWNUP : GET_SITUATION_SESSION,
    { variables: { id }, skip: !id, fetchPolicy: "cache-and-network" }
  );

  // A deleted session has to leave `decisionLab.sessions`, which Apollo cannot
  // work out from an `errors`-only payload.
  const [remove, { loading: removing }] = useMutation(DELETE_SITUATION_SESSION, {
    refetchQueries: ["GetDecisionLab", "GetDecisionLabForGrownUp", "GetLabSessions"],
  });
  const [reset, { loading: resetting }] = useMutation(
    RESET_SITUATION_SESSION_PERSONALIZATION
  );

  const session = (data as any)?.situationSession;
  const rows: Row[] = useMemo(() => session?.situations ?? [], [session]);

  const studentId = session?.studentId ?? (isGuardian ? params.get("student") : me?.id);
  const backTo = `/lab${isGuardian && studentId ? `?student=${studentId}` : ""}`;
  const linkTo = (key: string) =>
    `/lab/${key}?session=${id}${isGuardian && studentId ? `&student=${studentId}` : ""}`;

  const discard = async () => {
    try {
      const result: any = await remove({ variables: { sessionId: id } });
      const errors = result?.data?.deleteSituationSession?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }
      toast.success("Sesija ištrinta");
      navigate(backTo);
    } catch (error) {
      toast.error("Nepavyko ištrinti");
      console.error(error);
    }
  };

  const revert = async () => {
    try {
      const result: any = await reset({ variables: { sessionId: id } });
      const errors = result?.data?.resetSituationSessionPersonalization?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }
      toast.success("Grąžinti originalai");
    } catch (error) {
      toast.error("Nepavyko grąžinti");
      console.error(error);
    }
  };

  if (loading && !session) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="mx-auto max-w-2xl py-20 text-center text-muted-foreground">
        Sesija nerasta.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 pb-16">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to={backTo}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold tracking-tight">
            {session.done ? "Sesija atlikta" : "Situacijų sesija"}
          </h1>
          {/* The count, not a percentage — and the same sentence for both
              readers. */}
          <p className="text-muted-foreground">
            {session.doneCount} iš {session.size} atsakyta
            {session.personalized && (
              <span className="ml-2 inline-flex items-center gap-1 text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                pritaikyta
                {isGuardian && session.personalizationTheme
                  ? ` · ${session.personalizationTheme}`
                  : ""}
              </span>
            )}
          </p>
        </div>

        {isGuardian && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <SituationPersonalizeDialog
              sessionId={id}
              count={session.size}
              personalized={session.personalized}
              theme={session.personalizationTheme}
            />
            {session.personalized && (
              <Button variant="ghost" onClick={revert} disabled={resetting}>
                {resetting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Originalai
              </Button>
            )}
            <Button variant="outline" asChild>
              <Link to={`/lab/session/${id}/print`}>
                <Printer className="mr-2 h-4 w-4" />
                Spausdinti
              </Link>
            </Button>
            {/* Worth a confirmation, and worth saying what it does *not* take:
                a grown-up deleting an assignment should not have to wonder
                whether the child's writing goes with it. */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" disabled={removing}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Ištrinti šią sesiją?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Bus pašalinta tik pati sesija ir jai pritaikyti tekstai. Viskas,
                    ką mokinys parašė, lieka — atsakymai saugomi prie situacijos, ne
                    prie sesijos.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Atšaukti</AlertDialogCancel>
                  <AlertDialogAction onClick={discard}>Ištrinti</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {/* One tap into the next unanswered sheet. On a finished session there is
          nothing to continue to, and sending somebody back to the first sheet
          would read as their work having been lost. */}
      {session.nextKey && (
        <Button size="lg" className="w-full" asChild>
          <Link to={linkTo(session.nextKey)}>
            {session.started ? "Tęsti" : "Pradėti"}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      )}

      <div className="space-y-3">
        {rows.map((row, index) => {
          const done = row.answeredCount > 0;
          return (
            <Card key={row.key} className={done ? "border-primary/40" : undefined}>
              <CardContent className="flex items-center gap-4 py-4">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                    done
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {done ? <Check className="h-4 w-4" strokeWidth={3} /> : index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <Link
                    to={linkTo(row.key)}
                    className="font-semibold hover:underline"
                  >
                    {row.title}
                  </Link>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {row.themeLabels?.map((label) => (
                      <Badge key={label} variant="outline" className="font-normal">
                        {label}
                      </Badge>
                    ))}
                    {/* „Written in", never „right". */}
                    {done && (
                      <span className="text-sm text-muted-foreground">
                        parašyta {row.answeredCount} iš {row.questionCount}
                      </span>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="icon" asChild>
                  <Link to={linkTo(row.key)}>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
